import { useContext } from 'react'

import type { UseFormOptions } from '@domain-group/react-hooks'
import { useForm, useLatestEntity } from '@domain-group/react-hooks'

import { launchModal } from '@domain-group/javascript-utils'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import PageContext from '@contexts/pageContext'

import * as contactActions from '@redux/modules/contact'

import defaultFormState, { requiredFields } from '@models/contact'

import type { CampaignModel, ContactModel, ContactRequestOptions } from '@models/types'
import type { DeleteParams } from '@redux/modules/types'
import type { AppDispatch } from '@redux/store'
import type { UseCampaignContactPayload } from '@hooks/useCampaignContact'

type CreateContactParams = {
  contactParams: Partial<ContactModel>,
  dispatch: AppDispatch,
  requestOptions?: ContactRequestOptions,
}

const createContact = (params: CreateContactParams) => {
  const { dispatch, contactParams, requestOptions } = params
  const { createContact: createFn } = contactActions

  return dispatch(createFn(contactParams, requestOptions))
}

type UpdateContactParams = {
  contact: ContactModel,
  contactParams: Partial<ContactModel>,
  dispatch: AppDispatch,
  requestOptions?: ContactRequestOptions,
}

const updateContact = (params: UpdateContactParams) => {
  const {
    dispatch, contact, contactParams, requestOptions,
  } = params
  const { updateContact: updateFn } = contactActions

  const updatedParams = {
    id: contact.id,
    ...contactParams,
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

type CreateContactUserParams = {
  contact: ContactModel,
  dispatch: AppDispatch,
  requestOptions?: ContactRequestOptions,
}

const createContactUser = (params: CreateContactUserParams) => {
  const {
    dispatch, contact, requestOptions,
  } = params
  const { createContactUser: createContactFn } = contactActions

  return dispatch(createContactFn(contact, requestOptions))
}

type DeleteContactParams = {
  contact: DeleteParams<ContactModel>,
  dispatch: AppDispatch,
}

const deleteContact = (params: DeleteContactParams) => {
  const { dispatch, contact } = params
  const { deleteContact: deleteFn } = contactActions

  return dispatch(deleteFn(contact))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useContactForm(
  contact: Partial<ContactModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const contactForm = useForm(
    defaultFormState,
    { entity: contact, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [contact.id, contact.cache_key],
  )

  return {
    ...contactForm,
  }
}

export const useRelations = (contact: Partial<ContactModel> = {}) => {
  const { user_id } = contact

  const { users } = useSelector(reduxState => reduxState.entities)

  const user = user_id && users[user_id] ? users[user_id] : {}

  return {
    user,
  }
}

export type CreateOrEditContactCustomPayload = {
  callbacks?: {
    createFn?: UseCampaignContactPayload['callbacks']['createWithContact'],
    deleteFn?: UseCampaignContactPayload['callbacks']['deleteCampaignContact'],
  },
  campaign?: CampaignModel,
}

type UseContactOptions = {
  performHttpRequests?: boolean,
}

function useContact(
  initEntity: Partial<ContactModel> = {},
  options: UseContactOptions = {},
) {
  const { performHttpRequests } = options

  // @ts-expect-error TS2322
  const { entity: contact }: { entity: ContactModel} = useLatestEntity(initEntity, 'contacts')
  const { id } = contact

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  useReduxAction(
    'contacts',
    'loadContact',
    {
      entityId: id,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(id, actionRequestOptions),
      shouldPerformFn: () => !!performHttpRequests,
    },
  )

  const { user } = useRelations(contact)

  const {
    creating, deleting, loadedIds, loading, updating,
  } = useSelector(reduxState => reduxState.contacts)

  return {
    callbacks: {
      createContact: (contactParams: Partial<ContactModel>, entityOptions?: ContactRequestOptions) => (
        createContact({ contactParams, dispatch, requestOptions: entityOptions })
      ),
      createContactUser: (entityOptions?: ContactRequestOptions) => (
        createContactUser({ contact, dispatch, requestOptions: entityOptions })
      ),
      createOrEditContact: (customPayload?: CreateOrEditContactCustomPayload) => launchModal({
        callbacks,
        modalKey: 'CreateOrEditContactModal',
        payload: { ...customPayload, contact },
      }),
      deleteContact: () => deleteContact({ dispatch, contact }),
      updateContact: (contactParams: Partial<ContactModel>, entityOptions?: ContactRequestOptions) => (
        updateContact({
          contact, contactParams, dispatch, requestOptions: entityOptions,
        })
      ),
    },
    contact,
    creating,
    deleting,
    loading: loading && !loadedIds.includes(contact.id),
    updating,
    user,
  }
}

export type UseContactPayload = ReturnType<typeof useContact>

export default useContact
